import ComponentManager from '@/Management/ComponentManager';
import InlineEditingGridViewModel from '@/ViewModels/Controls/InlineEditingGridViewModel';
import ToolBarViewModel from '@/ViewModels/Controls/ToolBarViewModel';
import ToolBarViewModelBuilder from '@/Builder/ToolBarViewModelBuilder';
import ExtIEGFindAndConnectLinkViewModelT from '@/Components/ExtendedInlineEditingGrid/Links/ExtIEGFindAndConnectLinkViewModel';
import {
    IDVGItemDTO, IGenericDTO, IInlineEditingContextDataDTO, IInlineEditingContextDTO,
    IInlineEditingGridColumnDTO, IIteratorDTO
} from "coreframetypings/dto";
import ExtIEGDisconnectLinkViewModelT from '@/Components/ExtendedInlineEditingGrid/Links/ExtIEGDisconnectLinkViewModel';
import {
    FilterJson,
    IExtendedInlineEditingGenericDTO,
    IExtendedInlineEditingGridViewModelConstructorParameter
} from '#/DefaultViewExtensionInterfaces';
import { IControlFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import RefreshLinkViewModel from '@/Components/Links/RefreshLink/RefreshLinkViewModel';
import ExtIEGEditLinkViewModelT from '@/Components/ExtendedInlineEditingGrid/Links/ExtIEGEditLinkViewModel';
import TableConfigLinkViewModel from '@/Components/Links/TableConfigLink/TableConfigLinkViewModel';
import ListViewHelper from '@/Utility/ListViewHelper';
import InlineEditingGridColumnHelper from '@/Components/InlineEditingGrid/InlineEditingGridColumnHelper';
import RADApplication from '@/Management/RADApplication';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import FilterViewModel from '@/ViewModels/Controls/FilterViewModel';
import FilterHelper from '@/Utility/FilterHelper';
import FilterLinkViewModel from '@/Components/Links/FilterLink/FilterLinkViewModel';
import ExtIEGCreateAndConnectLinkViewModelT from '@/Components/ExtendedInlineEditingGrid/Links/ExtIEGCreateAndConnectLinkViewModel';
import StorageHelper from '@/Utility/StorageHelper';
import UserSpecificDataHelper from '@/Utility/UserSpecificDataHelper';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import LocatorHelper from '@/Utility/LocatorHelper';
import EditContext from '@/ViewModels/Contexts/EditContext';
import { IInlineValidationMessages } from '@/Components/ExtendedInlineEditingGrid/IInlineValidationMessage';
import ToolBarSpacerViewModel from '@/ViewModels/Controls/ToolBarSpacerViewModel';

export default class ExtendedInlineEditingGridViewModel extends InlineEditingGridViewModel {

    protected hiddenMembers: string[];
    protected additionalMembers: string[];
    public userSpecificColumnDefinition: KnockoutObservable<IInlineEditingGridColumnDTO[]>;
    public toolBar: ToolBarViewModel;
    public filter: FilterViewModel;
    private primarySubAssociation: string;
    public uniqueSubAssociation: boolean;
    private editSubAssociation: boolean;
    public isSelectable: KnockoutObservable<boolean>;
    public selected: KnockoutObservableArray<IGenericDTO>;
    public page: KnockoutObservable<number>;
    public pageSize: KnockoutObservable<number>;
    public pageSizes: KnockoutObservable<number[]>;
    public dataSource: KnockoutObservableArray<IExtendedInlineEditingGenericDTO>;
    public notDisconnectableIds: KnockoutObservable<string[]>;
    private disconnectLinkVm: ExtIEGDisconnectLinkViewModelT;
    public defaultHeight: KnockoutObservable<string>;
    private _toolBarLocator: KnockoutComputed<string>;
    private lastRefresh: { filter?: FilterJson }
    public isMultiSelection: boolean;
    private selfValidation: boolean;
    private currentValidationMessages: IInlineValidationMessages[];

    // Dieses bool steuert ebenso, ob eine Navigation im Edit Fall durchgeführt werden soll
    public navigateOnCreate: boolean;

    public constructor(protected params: IExtendedInlineEditingGridViewModelConstructorParameter) {
        super($.extend({}, params, {
            autoInit: false
        }));

        this.columns.extend({ throttle: 200 });
        this.primarySubAssociation = this.paramOrDefault(params.primarySubAssociation, '');
        this.editSubAssociation = this.paramOrDefault(params.editSubAssociation, false);
        this.uniqueSubAssociation = this.paramOrDefault(params.uniqueSubAssociation, false);
        this.isSelectable = this.observableOrDefault(params.isSelectable, false);
        this.selected = this.observableOrDefault(params.selected, []) as KnockoutObservableArray<IGenericDTO>;
        this.editMode = 'multiple';
        this.hiddenMembers = this.paramOrDefault(params.hiddenMembers, []);
        this.additionalMembers = this.paramOrDefault(params.additionalMembers, []);
        this.page = this.observableOrDefault(params.page, 1);
        this.pageSize = UserSpecificDataHelper.loadUserSpecificData(this.detailViewItem.listView.name + '_PageSize', RADApplication.getInstance().configuration.possibleAssoziationListViewPages[0]);
        this.pageSizes = this.observableOrDefault(params.pageSizes, RADApplication.getInstance().configuration.possibleAssoziationListViewPages);
        this.defaultHeight = this.observableOrDefault(GlobalConfiguration.dataGrid.HeightForAssociationGridInDV, '100%');
        this.height = this.observableOrDefault(params.height,
            this.defaultHeight(),
            (value) => {
                if (value.indexOf("%") == -1) {
                    let int = parseInt(value, 10);
                    if (isNaN(int) || int < GlobalConfiguration.dataGrid.MinHeightForAssociationGridInDV) {
                        return GlobalConfiguration.dataGrid.HeightForAssociationGridInDV;
                    }
                    return int + "px";
                }
                else
                    return value;
            });
        this.navigateOnCreate = this.paramOrDefault(params.navigateOnCreate, true);

        this.isMultiSelection = this.paramOrDefault(params.isMultiSelection, true);
        this.notDisconnectableIds = this.observableOrDefault([]);
        this.selfValidation = this.paramOrDefault(params.selfValidation, true);

        this._toolBarLocator = LocatorHelper.buildLocator(this.locator, 'toolbar');
        this.toolBar = this.createToolbar();
        this.filter = this.createFilter();
        this.lastRefresh = {
            filter: null
        };
         
        this.init();
    }

    public onError(messages: IInlineValidationMessages[]): void {
        if (this.selfValidation)
            return;

        this.currentValidationMessages = messages;

        if (!messages || messages.length === 0) {
            const contexts = this.getEditContexts();
            contexts.forEach(x => x.setValidationErrors([]));
        }
        else {
            const contexts = this.getEditContexts();
            if (!contexts)
                return;

            messages.forEach(m => {
                const context = contexts.filter(x => x.genericObject.pdoId === m.pdoId)[0];
                if (!context)
                    return;

                const validationMessages = m.messages.map(x => {
                    return {
                        context: context,
                        property: x.property,
                        message: x.message,
                    };
                });

                context.setValidationErrors(validationMessages);
            });
        }
    }

    public showDeletionWarningOnDisconnect(show: boolean): void {
        if (this.disconnectLinkVm)
            this.disconnectLinkVm.showDeletionWarningOnDisconnect = show;
    }

    /**
     * @override
     */
    protected init(): JQuery.Promise<boolean> {
        // Die Subscriptions werden erst erstellt, wenn das Grid initalisiert wurde
        // sodass erst Änderungen, die durch den User vorgenommen werden, durch die Subscriptions erkannt werden
        // und nicht bei jeder Änderung während der Initialisierung schon ausgeführt werden.
        return super.init().then((result) => {
            if (result)
                if (!this.isDisposed)
                    this.registerSubscriptions();

            return result;
        }).promise();
    }

    private createToolbar(): ToolBarViewModel {
        this.disconnectLinkVm = this.createDisconnectLink();
        return new ToolBarViewModelBuilder().withPrimaryLinks([
            this.createCreateAndConnectLink(),
            this.createFindAndConnectLink(),
            this.createEditLink(),
            new ToolBarSpacerViewModel({}),
            this.disconnectLinkVm,
        ]).withSecondaryLinks([
            new ToolBarSpacerViewModel({}),
            this.createRefreshLink(),
            this.createFilterLink(),
            this.createTableConfigLink(),
        ]).withParentLocator(this.locator).build();
    }

    private createFilter(): FilterViewModel {
        const filter = new FilterViewModel({
            parentLocator: this.locator,
            pdTypeName: this.detailViewItem.listView.typeName,
            includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
            includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
            includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
            listViewPdoId: this.detailViewItem.listView.listViewID,
            locator: 'datagrid.filter',
            columns: this.columns,
            onSubmit: () => {
                this.refresh();
            },
            shouldShowChips: false,
            shouldShowSaveInputs: false,
            shouldShowSorting: false,
            publicUserData: this.detailViewContext.publicUserData["LV: " + this.detailViewItem.listView.typeName]
        });
        this.defineSubscription(filter.isLoading, (isLoading) => {
            if (!this.isDisposed)
                isLoading ? this.increaseBusyCounter() : this.decreaseBusyCounter();
        });

        return filter;
    }

    private createCreateAndConnectLink(): ExtIEGCreateAndConnectLinkViewModelT {
        const ExtIEGCreateAndConnectLinkViewModel = ComponentManager.getViewModel<typeof ExtIEGCreateAndConnectLinkViewModelT>('ExtIEGCreateAndConnectLink');
        return new ExtIEGCreateAndConnectLinkViewModel({
            context: this.detailViewContext,
            listView: this.detailViewItem.listView,
            inlineEditingContext: this.inlineEditingContext,
            dataSource: this,
            isEnabled: this.isEnabled,
            parentLocator: this._toolBarLocator,
            navigateOnCreate: this.navigateOnCreate,
            isVisible: this.defineComputed(() => this.detailViewItem.listView.showButtonCreate && this.hasAddRight()),
            showToolBarText: false
        });
    }

    private createFindAndConnectLink(): ExtIEGFindAndConnectLinkViewModelT {
        const ExtIEGFindAndConnectLinkViewModel = ComponentManager.getViewModel<typeof ExtIEGFindAndConnectLinkViewModelT>('ExtIEGFindAndConnectLink');
        return new ExtIEGFindAndConnectLinkViewModel({
            pdTypeName: this.defineComputed(() => {
                return this.inlineEditingContext()?.primarySubAssociationListView?.typeName;
            }),
            context: this.detailViewContext,
            listView: this.detailViewItem.listView,
            inlineEditingContext: this.inlineEditingContext,
            dataSource: this,
            isEnabled: this.isEnabled,
            uniqueSubAssociation: this.uniqueSubAssociation,
            detailViewItem: this.detailViewItem,
            parentLocator: this._toolBarLocator,
            busyCallback: (busy: boolean) => {
                if (busy)
                    this.increaseBusyCounter();
                else
                    this.decreaseBusyCounter();
            },
            showToolBarText: false,
        });
    }

    private createEditLink(): ExtIEGEditLinkViewModelT {
        const ExtIEGEditLinkViewModel = ComponentManager.getViewModel<typeof ExtIEGEditLinkViewModelT>('ExtIEGEditLink');
        return new ExtIEGEditLinkViewModel({
            context: this.detailViewContext,
            inlineEditingContext: this.inlineEditingContext,
            listView: this.detailViewItem.listView,
            dataSource: this,
            primarySubAssociation: this.primarySubAssociation,
            editSubAssociation: this.editSubAssociation,
            parentLocator: this._toolBarLocator,
            isEnabled: this.isSummaryViewMode ? null : this.navigateOnCreate, // null --> Default Value --> Ist der Edit Button sichtbar?
            showToolBarText: false
        });
    }

    private createDisconnectLink(): ExtIEGDisconnectLinkViewModelT {
        const ExtIEGDisconnectLinkViewModel = ComponentManager.getViewModel<typeof ExtIEGDisconnectLinkViewModelT>('ExtIEGDisconnectLink');
        return new ExtIEGDisconnectLinkViewModel({
            context: this.detailViewContext,
            listView: this.detailViewItem.listView,
            dataSource: this,
            showDeletionWarningOnDisconnect: true,
            inlineEditingContext: this.inlineEditingContext,
            isEnabled: this.defineComputed(() => this.isDisconnectable()),
            detailViewItem: this.detailViewItem,
            parentLocator: this._toolBarLocator,
            showToolBarText: false
        });
    }

    private isDisconnectable(): boolean {
        if (!this.isEnabled())
            return false;

        const selected = this.selected();
        if (!selected)
            return false;

        const notDisconnectableIds = this.notDisconnectableIds();
        if (!notDisconnectableIds)
            return true;

        return _.all(selected, (x) => _.all(notDisconnectableIds, (y) => x.pdoId != y));
    }

    private createRefreshLink(): RefreshLinkViewModel {
        return new RefreshLinkViewModel({
            showToolBarText: false,
            showErgname: false,
            dataSource: this,
            parentLocator: this._toolBarLocator
        });
    }

    private createFilterLink(): FilterLinkViewModel {
        return new FilterLinkViewModel({
            showToolBarText: false,
            dataSource: this,
            listView: this.detailViewItem.listView,
            parentLocator: this._toolBarLocator
        });
    }

    private createTableConfigLink(): TableConfigLinkViewModel {
        return new TableConfigLinkViewModel({
            showToolBarText: false,
            showErgname: false,
            detailViewItem: this.detailViewItem,
            columnDefinition: this.userSpecificColumnDefinition,
            defaultColumnDefinition: this.columnDefinition,
            parentLocator: this._toolBarLocator,
            storageColumnDefinitionKey: this.detailViewItem.listView.name + '_IEG_ColumnDefinition'
        });
    }

    public onSelectionChanged(rows: Array<IGenericDTO>): void {
        if (this.isDisposed)
            return;

        this.selected(rows);
    }

    /**
     * @override
     */
    protected onBeforeApplyDetailViewContext(): JQueryPromise<boolean> {
        let success = $.Deferred();

        const editContexts = this.dataSource()
            .filter(x => x.hasLock)
            .map(row => this.getEditContextForRow(row));

        let results = [];
        editContexts.forEach((context) => {
            const result = this.selfValidation
                ? context.updateAndValidateProperties()
                : context.updateProperties();

            results.push(result);
        });

        $.when.apply($, results).then((...allResults: boolean[]) => {
            if (_.all(allResults, x => x == true)) {
                success.resolve(true);
            }
            else {
                success.resolve(false);
            }
        });

        return success.promise();
    }

    /**
     * @override
     */
    protected createColumnDefinition(inlineEditingContext: IInlineEditingContextDTO) {
        return InlineEditingGridColumnHelper.createColumnDefinitionFromAssociationListView(this.detailViewItem.listView, inlineEditingContext.detailView, {
            hiddenMembers: this.hiddenMembers,
            excludedMembers: this.excludedMembers,
            additionalMembers: this.additionalMembers,
            visibleMembers: this.visibleMembers
        });
    }

    /**
     * @override
     */
    protected transformRows(result: IInlineEditingContextDataDTO): IExtendedInlineEditingGenericDTO[] {
        return result.subjects.map((row: IExtendedInlineEditingGenericDTO) => {
            row.hasLock = result.subjectLocks[row.pdoId] || false;
            return row;
        });
    }

    /**
     * @override
     */
    protected getDefaultFactoryParametersForEditValue(row: IExtendedInlineEditingGenericDTO, item: IDVGItemDTO): IControlFactoryConstructorParameter {
        let params = super.getDefaultFactoryParametersForEditValue(row, item);
        params.isEnabled = this.defineComputed(() => row.hasLock && this.isEnabled());

        return params;
    }

    /**
     * @override
     */
    protected getCellLocator(cell: HTMLElement): ObservableOrDefault<string> {
        return cell.dataset['locator'];
    }

    /**
     * @override
     */
    protected registerColumnDefinitionSubscriptions(): void {
        let storageValue = StorageHelper.bind(this.detailViewItem.listView.name + '_IEG_ColumnDefinition', this.columnDefinition());
        this.userSpecificColumnDefinition = this.defineComputed({
            read: () => {
                const defaultColumnDefinition = this.columnDefinition();
                return ListViewHelper.loadColumnDefinitions(this.detailViewItem.listView.name + '_IEG_ColumnDefinition', defaultColumnDefinition, [], defaultColumnDefinition.length > 0)();
            },
            write: (newValue) => {
                storageValue(newValue);
            }
        });

        this.defineSubscription(this.userSpecificColumnDefinition, (columnDefinition) => {
            let columns = this.createColumns(columnDefinition);
            this.columns(columns);
        });
    }

    private registerSubscriptions(): void {
        // refresh grid on column change
        this.defineSubscription(this.columns,
            () => {
                if (!this.isDisposed)
                    this.refresh();
            });

        // refresh grid on filter change
        this.defineComputed(() => {
            if (!this.isDisposed) {
                // dependency
                let filter = this.getFilterExpression();

                // break on initial call OR if the filter hasn't changed
                if (ko.computedContext.isInitial() || JSON.stringify(this.lastRefresh.filter) == JSON.stringify(filter))
                    return;

                this.refresh();
            }
        });
    }

    /**
    * @override
    */
    public refresh(): JQuery.Promise<boolean> {
        this.destroyAllAsync();
        return super.refresh();
    }

    /**
    * @override
    */
    protected onAfterRefresh(): void {
        this.onError(this.currentValidationMessages);
    }

    /**
     * @override
     */
    protected getPropertiesForIterator(): string[] {
        let properties = super.getPropertiesForIterator();
        if (this.editSubAssociation)
            properties.push(this.primarySubAssociation + '.PDO_ID');

        return properties;
    }

    /**
     * @override
     */
    protected getProperties(): string[] {
        let properties = super.getProperties();
        if (this.editSubAssociation)
            properties.push(this.primarySubAssociation + '.PDO_ID');

        return properties;
    }

    /**
     * @override
     */
    protected shouldRemoveDefaultProperties(): boolean {
        return false;
    }

    /**
     * @override
     */
    protected createIterator(): JQuery.Promise<IIteratorDTO> {
        const filter = this.getFilterExpression();
        this.lastRefresh.filter = filter;
        const context = this.inlineEditingContext();

        return RADEnvironmentProxy.getInstance().createIteratorForAssoziation({
            pdo_id: context?.contextObjectPdoId,
            assoziationName: context?.contextRelationName,
            pdTypeName: context?.contextObjectPdTypeName,
            filter: JSON.stringify(filter),
            orderby: this.orderBy(),
            properties: this.getPropertiesForIterator(),
            retrieveExtentCount: true,
            itOperator: null
        }).promise();
    }

    /**
     * @override
     */
    protected getObjects(): JQuery.Promise<IInlineEditingContextDataDTO> {
        const start = this.calcStartPoint(this.page(), this.pageSize());
        const end = this.calcEndPoint(start, this.pageSize());
        const context = this.inlineEditingContext();

        return RADEnvironmentProxy.getInstance().getInlineEditingObjects({
            inlineEditingContextId: context?.id,
            iteratorId: this.iterator?.id,
            properties: this.getProperties(),
            start: start,
            end: end
        }).promise();
    }

    private getFilterExpression(): FilterJson {
        return FilterHelper.createFilterObject(this.detailViewItem.listView.filter, '', this.filter)
    }

    /**
     * @override
     */
    protected getCreateContextParameter(): any { //Parameters<typeof RADEnvironmentProxy.createInlineEditingContext>[0] {
        let params = super.getCreateContextParameter();
        params.acquireLocks = !this.detailViewContext.isSummaryViewMode() && !this.detailViewContext.isLocked() && !this.detailViewContext.genericObject.isHistorized;
        params.primarySubAssociation = this.primarySubAssociation;

        return params;
    }

    public onPageSizeChanged(newPageSize: number): void {
        if (this.pageSize() != newPageSize) {
            this.pageSize(newPageSize);
            this.page(1);
            this.refresh();
        }
    }

    public onPageChanged(newPage: number): void {
        if (this.page() != newPage) {
            this.page(newPage);
            this.refresh();
        }
    }

    private calcStartPoint(page: number, pageSize: number): number {
        let start = (page - 1) * pageSize;
        while (start >= this.totalRows()) {
            if (page <= 0) {
                break;
            }
            start = this.calcStartPoint(--page, pageSize);
        }

        return start;
    }

    protected calcEndPoint(start: number, pageSize: number): number {
        let end = start + pageSize - 1;
        if (end < 0)
            end = 0;

        return end;
    }
}

ComponentManager.setViewModel('ExtendedInlineEditingGridViewModel', ExtendedInlineEditingGridViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');